import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Stack,
} from '@mui/material';
import { toast } from 'sonner';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { GOAL_DESCRIPTION_MAX, GOAL_METRIC_OPTIONS, GOAL_NAME_MAX } from '../../types/analytics';
import type { Goal, GoalCreate, GoalStatus, GoalType } from '../../types/analytics';

interface GoalFormProps {
  open: boolean;
  goal: Goal | null;
  onClose: () => void;
}

const STATUS_OPTIONS: GoalStatus[] = ['active', 'achieved', 'abandoned'];

const GoalForm: React.FC<GoalFormProps> = ({ open, goal, onClose }) => {
  const { createGoal, updateGoal } = useAnalyticsStore();

  const [goalType, setGoalType] = useState<GoalType>('numeric');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [metricName, setMetricName] = useState<string>('');
  const [targetValue, setTargetValue] = useState<string>('');
  const [targetDate, setTargetDate] = useState<string>('');
  const [status, setStatus] = useState<GoalStatus>('active');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when opening / switching goals
  useEffect(() => {
    if (!open) return;
    if (goal) {
      setGoalType(goal.goal_type);
      setName(goal.name);
      setDescription(goal.description ?? '');
      setMetricName(goal.metric_name ?? '');
      setTargetValue(goal.target_value !== null ? String(goal.target_value) : '');
      setTargetDate(goal.target_date ?? '');
      setStatus(goal.status);
    } else {
      setGoalType('numeric');
      setName('');
      setDescription('');
      setMetricName('');
      setTargetValue('');
      setTargetDate('');
      setStatus('active');
    }
  }, [open, goal]);

  const trimmedName = name.trim();
  const nameOver = name.length > GOAL_NAME_MAX;
  const descOver = description.length > GOAL_DESCRIPTION_MAX;
  const numericMissing = goalType === 'numeric' && (!metricName || targetValue.trim() === '');
  // Reject NaN, zero, and negative targets. Zero makes the progress bar
  // permanently empty (division by zero guarded downstream) and negative
  // values break the "lower is better" math.
  const parsedTargetValue = Number(targetValue);
  const targetValueInvalid =
    goalType === 'numeric' &&
    targetValue.trim() !== '' &&
    (Number.isNaN(parsedTargetValue) || parsedTargetValue <= 0);

  const canSubmit =
    trimmedName.length > 0 &&
    !nameOver &&
    !descOver &&
    !numericMissing &&
    !targetValueInvalid &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload: GoalCreate = {
        goal_type: goalType,
        name: trimmedName,
        description: description.trim() ? description.trim() : null,
        metric_name: goalType === 'numeric' ? metricName : null,
        target_value: goalType === 'numeric' ? Number(targetValue) : null,
        target_date: targetDate || null,
        status,
      };
      if (goal) {
        await updateGoal(goal.id, payload);
        toast.success('Goal updated');
      } else {
        await createGoal(payload);
        toast.success('Goal created');
      }
      onClose();
    } catch (err) {
      console.error('Failed to save goal:', err);
      toast.error(goal ? 'Failed to update goal' : 'Failed to create goal');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{goal ? 'Edit goal' : 'New goal'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <RadioGroup
            row
            value={goalType}
            onChange={(e) => setGoalType(e.target.value as GoalType)}
          >
            <FormControlLabel value="numeric" control={<Radio size="small" />} label="Numeric" />
            <FormControlLabel
              value="qualitative"
              control={<Radio size="small" />}
              label="Qualitative"
            />
          </RadioGroup>

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={nameOver}
            helperText={`${name.length}/${GOAL_NAME_MAX}${nameOver ? ' — too long' : ''}`}
            inputProps={{ 'data-testid': 'goal-name-input' }}
            fullWidth
            size="small"
            required
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={descOver}
            helperText={`${description.length}/${GOAL_DESCRIPTION_MAX}${
              descOver ? ' — too long' : ''
            }`}
            inputProps={{ 'data-testid': 'goal-description-input' }}
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
          />

          {goalType === 'numeric' && (
            <>
              <TextField
                select
                label="GA4 metric"
                value={metricName}
                onChange={(e) => setMetricName(e.target.value)}
                fullWidth
                size="small"
                required
                inputProps={{ 'data-testid': 'goal-metric-select' }}
              >
                {GOAL_METRIC_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.lowerIsBetter ? ' (lower is better)' : ''}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Target value"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                type="number"
                error={targetValueInvalid}
                helperText={targetValueInvalid ? 'Must be a positive number' : ''}
                fullWidth
                size="small"
                required
                inputProps={{ 'data-testid': 'goal-target-value-input', min: 0 }}
              />
            </>
          )}

          <TextField
            label="Target date"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />

          {goal && (
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as GoalStatus)}
              fullWidth
              size="small"
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          data-testid="goal-submit-btn"
        >
          {submitting ? 'Saving…' : goal ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoalForm;
